import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FolderDetail from './FolderDetail';
import { getOrganizationFromPath, getOrganizationFolderId } from '../utilities/organizationUtils';

const Sidebar = ({ onFileSelect, setSearchFiles, onFolderSelect, folderIdFromUrl, allFolders, onClearSearch }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const currentOrg = getOrganizationFromPath(location.pathname);
  const isOrgFrontpage = currentOrg && location.pathname === `/org/${currentOrg}`;
  const orgRootId = currentOrg ? getOrganizationFolderId(currentOrg) : null;

  // If we're on the org frontpage, inject the org root ID so FolderDetail expands that node.
  const effectiveFolderIdFromUrl = isOrgFrontpage && orgRootId ? orgRootId : folderIdFromUrl;

  const toggleSidebar = () => setIsOpen(!isOpen);
  const closeSidebar = () => setIsOpen(false);

  // Custom handler to check if clicking on org root folder
  const handleFolderSelect = (folderId, folderUrl, folderName) => {
    // If we're in an org context and clicking on the org root folder, navigate to org frontpage
    if (currentOrg && folderId === orgRootId) {
      // Clear search results first if callback provided
      if (onClearSearch) {
        onClearSearch();
      }
      // Only navigate, don't call onFolderSelect (which would load files)
      navigate(`/org/${currentOrg}`);
      closeSidebar();
      return;
    }
    
    // For all other folders, proceed normally
    onFolderSelect(folderId, folderUrl, folderName);
    closeSidebar();
  };

  return (
    <>
      {/* Burger Menu Button - Mobile Only - Hidden when menu is open */}
      {!isOpen && (
        <button
          onClick={toggleSidebar}
          className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-white shadow-md border border-gray-200 hover:bg-gray-50"
          aria-label="Toggle menu"
        >
          <svg
            className="w-6 h-6 text-gray-700"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Overlay - Mobile Only */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:relative
          top-1 left-0
          h-full
          w-72
          overflow-y-auto
          border-r border-gray-200
          bg-white
          px-4 py-6
          shadow-sm
          z-40
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Emner</h2>
          
          {/* Close button - Mobile Only */}
          <button
            onClick={closeSidebar}
            className="lg:hidden p-1 rounded-md hover:bg-gray-100"
            aria-label="Close menu"
          >
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!allFolders ? (
          <div className="text-gray-500">Henter emner...</div>
        ) : (
          <div className="space-y-2">
            {allFolders.Children.map(folder => (
              <FolderDetail
                key={folder.Id}
                item={folder}
                onFileSelect={onFileSelect}
                onFolderSelect={handleFolderSelect}
                folderIdFromUrl={effectiveFolderIdFromUrl}
                allFolders={allFolders}
              />
            ))}
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;